import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { IsOptional, IsString } from 'class-validator';
import { action, makeAutoObservable, observable } from 'mobx';
import { toast } from 'react-toastify';

import type { CreateRoleDto, RoleDto } from '@/shared/application/interfaces/pegasus-io.gateway';
import { ObservableForm } from '@/shared/presentation/forms/BaseForm';

import { CreateRoleCollaboration } from '../../application/collaborations/create-role.collaboration';
import { GetRolesCollaboration } from '../../application/collaborations/get-roles.collaboration';
import { RemoveRoleCollaboration } from '../../application/collaborations/remove-role.collaboration';
import { UpdateRoleCollaboration } from '../../application/collaborations/update-role.collaboration';

class Form {
  @IsOptional()
  guid: string;

  @IsString({ message: 'Campo obrigatório' })
  name: string;

  @IsString({ message: 'Campo obrigatório' })
  slug: string;

  @IsOptional()
  description: string;
}

@ViewModel({ singleton: true })
export class RolesViewModel implements IReactiveModel {
  @observable form = new ObservableForm(Form);
  @observable roles: RoleDto[] = [];
  @observable isCreateRoleModalOpen = false;
  @observable isLoading = false;
  @observable hasError = false;
  @observable isSubmitting = false;
  @observable isRemoving = false;
  @observable isUpdating = false;
  @observable isUpdateRoleModalOpen = false;

  constructor(
    private getRolesCollaboration: GetRolesCollaboration,
    private createRoleCollaboration: CreateRoleCollaboration,
    private removeRoleCollaboration: RemoveRoleCollaboration,
    private updateRoleCollaboration: UpdateRoleCollaboration,
  ) {
    makeAutoObservable(this);
  }

  @action
  async onInit() {
    try {
      this.isLoading = true;
      const { data } = await this.getRolesCollaboration.run();
      this.roles = data;
    } catch (error) {
      console.error(error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async create() {
    try {
      this.isSubmitting = true;

      const dto: CreateRoleDto = {
        name: this.form.values.name,
        slug: this.form.values.slug,
        description: this.form.values.description,
      };

      const { data } = await this.createRoleCollaboration.run(dto);
      this.roles = [...this.roles, data];

      this.form.reset();

      this.isCreateRoleModalOpen = false;
      toast.success('Papel criado com sucesso');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao criar papel');
    } finally {
      this.isSubmitting = false;
    }
  }

  async updateRole() {
    try {
      this.isUpdating = true;

      const dto = {
        guid: this.form.values.guid,
        name: this.form.values.name,
        slug: this.form.values.slug,
        description: this.form.values.description,
      };

      await this.updateRoleCollaboration.run(dto);

      this.roles = this.roles.map((role) => {
        if (role.guid === dto.guid) {
          return {
            ...role,
            description: dto.description,
          };
        }

        return role;
      });

      this.form.reset();

      this.isUpdateRoleModalOpen = false;

      toast.success('Papel atualizado com sucesso');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao atualizar papel');
    } finally {
      this.isUpdating = false;
    }
  }

  async removeRole(roleGuid: string) {
    try {
      this.isRemoving = true;
      await this.removeRoleCollaboration.run(roleGuid);
      this.roles = this.roles.filter((role) => role.guid !== roleGuid);
      toast.success('Papel removido com sucesso');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao remover papel');
    } finally {
      this.isRemoving = false;
    }
  }

  @action
  openCreateRoleModal() {
    this.isCreateRoleModalOpen = true;
  }

  @action
  closeCreateRoleModal() {
    this.isCreateRoleModalOpen = false;
  }

  @action
  openUpdateRoleModal(role: RoleDto) {
    this.form.setValue('guid', role.guid);
    this.form.setValue('name', role.name);
    this.form.setValue('slug', role.slug);
    this.form.setValue('description', role.description);

    this.isUpdateRoleModalOpen = true;
  }

  @action
  closeUpdateRoleModal() {
    this.form.reset();

    this.isUpdateRoleModalOpen = false;
  }
}
